import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { IconCustomAurora } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Button,
  CircularProgress,
  Grow,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import {
  fallbackLocale,
  getLocaleOptions,
  IntlLocale,
  LocaleOption,
} from '../../../../i18n/config';
import { annotationSelector } from '../../../../redux/modules/annotation/selectors';
import {
  allMessagesSelector,
  datapointsSelector,
} from '../../../../redux/modules/datapoints/selector';
import { additionalLocalesEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import { useQueue } from '../../../queues/hooks/useQueue';
import { useGenerateRejectionEmail } from '../EmailResponseForm/hooks/useGenerateRejectionEmail';
import { EDITOR_STATE_EMPTY_VALUE } from '../EmailResponseForm/hooks/useGetFormValues';
import { getErrors, getUniqErrorMessages } from './helpers';

type Props = {
  handleGeneratedMessage: (message: string) => void;
  annotationUrl: string;
  editorStateHtmlValue: string;
  queueUrl: string;
};

const resolveDefaultLanguage = (
  queueLocale: string | undefined,
  userLocale: string,
  localeOptions: Array<LocaleOption>
) => {
  const queueLocalePrefix =
    queueLocale && queueLocale !== 'auto'
      ? (queueLocale.split(/[_-]/)[0] ?? queueLocale)
      : undefined;

  return (
    localeOptions.find(o => o.value === queueLocalePrefix)?.value ??
    localeOptions.find(o => o.value === userLocale)?.value ??
    fallbackLocale
  );
};

const GenerateRejectionEmail = ({
  handleGeneratedMessage,
  annotationUrl,
  editorStateHtmlValue,
  queueUrl,
}: Props) => {
  const intl = useIntl();
  const isInitialState = editorStateHtmlValue === EDITOR_STATE_EMPTY_VALUE;
  const additionalLocalesEnabled = useSelector(
    additionalLocalesEnabledSelector
  );
  const localeOptions = useMemo(
    () => getLocaleOptions({ additionalLocalesEnabled }),
    [additionalLocalesEnabled]
  );

  // Resolve language for dropdown default
  const { data: queue } = useQueue(getIDFromUrl(queueUrl));
  const [language, setLanguage] = useState<IntlLocale>(() =>
    resolveDefaultLanguage(queue?.locale, intl.locale, localeOptions)
  );

  // Get messages
  const allDatapoints = useSelector(datapointsSelector);
  const allMessages = useSelector(allMessagesSelector);
  const annotationMessages = useSelector(annotationSelector)?.messages ?? [];

  const errorMessages = getUniqErrorMessages(
    getErrors({ ...allMessages, annotation: annotationMessages }),
    allDatapoints
  );

  // Render generated email message
  const [isRenderingMessage, setIsRenderingMessage] = useState(false);

  const {
    mutate: generate,
    isLoading,
    error: generationError,
  } = useGenerateRejectionEmail(message => {
    // The code below imitates how ChatGPT is rendering the generated messages
    // Incrementaly adding parts of the message
    setIsRenderingMessage(true);
    const splittedString = message.split('');

    let index = 0;

    const intervalId = setInterval(
      () => {
        handleGeneratedMessage(splittedString.slice(0, index + 1).join(''));

        index += 1;

        if (index === splittedString.length) {
          setIsRenderingMessage(false);
          clearInterval(intervalId);
        }
      },
      // Short email is always on — use faster rendering interval
      10
    );
  });

  const onSubmit = () =>
    generate({
      annotation: annotationUrl,
      messages: errorMessages,
      options: {
        short: true,
        friendly: false,
        regenerate: !isInitialState,
        language,
      },
    });

  const isGenerating = isLoading || isRenderingMessage;

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
      >
        <TextField
          select
          size="small"
          value={language}
          onChange={e => {
            const next = localeOptions.find(
              o => o.value === e.target.value
            )?.value;
            if (next) setLanguage(next);
          }}
          sx={{ width: 'fit-content' }}
          data-cy="generate-email-language-select"
        >
          {localeOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          color="aurora"
          data-cy={
            isInitialState
              ? 'btn-generate-rejection-email-write'
              : 'btn-generate-rejection-email-re-write'
          }
          startIcon={
            isGenerating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SvgIcon fontSize="large">
                <IconCustomAurora />
              </SvgIcon>
            )
          }
          disabled={isGenerating}
          size="medium"
          onClick={onSubmit}
          sx={{
            alignSelf: 'center',
          }}
        >
          {isGenerating
            ? intl.formatMessage({
                id: 'components.emailResponseForm.autoCompose.button.inProgress',
              })
            : isInitialState
              ? intl.formatMessage({
                  id: 'components.emailResponseForm.autoCompose.button.label',
                })
              : intl.formatMessage({
                  id: 'components.emailResponseForm.autoCompose.button.label.rewrite',
                })}
        </Button>
      </Stack>
      <Grow in={generationError?.code === 429} unmountOnExit>
        <Alert variant="standard" severity="error">
          {intl.formatMessage({
            id: 'components.emailResponseForm.autoCompose.throttleError',
          })}
        </Alert>
      </Grow>
    </Stack>
  );
};

export { GenerateRejectionEmail };
